import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTableColumns } from '@/hooks/useTableColumns'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { ColumnCells, ResizableTh } from '@/components/table'
import { Loader2, AlertCircle, Search, ChevronLeft, ChevronRight, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import type { FormSubmission } from '@/types/cgt'
import { formsApi, type SubmissionWithFormName } from '@/api/formsApi'
import { StartCollectionFormDialog } from '@/components/collection/StartCollectionFormDialog'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { FEATURES } from '@/lib/featureFlags'
import { useFeatureFlag } from '@/providers/FeatureFlagsProvider'
import { PageHeading } from '@/components/layout/PageHeading'
import { sortParam } from '@/lib/tableSort'
import { formatRelativeTime } from '@/lib/relativeTime'
import { cn } from '@/lib/utils'

/**
 * The Collection page (Home tile "Collection", route /forms), z8nrz7f9j7.
 *
 * Lists every submission of a form whose Form Type is Collection, newest
 * first, with a patient MRN filter. The category filter is applied by the
 * server: the list is paged, so filtering a page here would hide matches on
 * other pages and leave the footer count wrong.
 *
 * A row opens the form on its own page (/forms/:submissionId, the standalone
 * form page), not in a dialog.
 *
 * It replaced a per-patient view of the legacy 8-step form chain. That view
 * matched on the legacy `formType`, which forms made in the Form Builder never
 * set, so it showed nothing for them.
 */

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    active: 'bg-blue-100 text-blue-800 border-blue-200',
    pending_review: 'bg-amber-100 text-amber-800 border-amber-200',
    completed: 'bg-green-100 text-green-800 border-green-200',
    cancelled: 'bg-gray-100 text-gray-600 border-gray-200',
  }
  const labels: Record<string, string> = {
    active: 'In progress',
    pending_review: 'Pending review',
    completed: 'Completed',
    cancelled: 'Cancelled',
  }
  return (
    <Badge className={`text-xs border ${styles[status] ?? 'bg-gray-100 text-gray-700'}`}>
      {labels[status] ?? status}
    </Badge>
  )
}

const COLLECTION_TABLE_ID = 'collection-submissions'

// `sortable` ids are the backend's sort keys (SUBMISSION_SORTS). The Form
// column is not sortable: that key orders by the legacy form type, which
// Collection forms do not carry.
const COLLECTION_COLUMNS: TableColumnDef[] = [
  { id: 'patientMrn', header: 'Patient', locked: true, defaultWidth: 220, sortable: true },
  { id: 'form', header: 'Form', defaultWidth: 260 },
  { id: 'status', header: 'Status', defaultWidth: 140, sortable: true },
  { id: 'created', header: 'Started', defaultWidth: 170, sortable: true },
  { id: 'updated', header: 'Last updated', defaultWidth: 150, sortable: true },
  { id: 'completed', header: 'Completed by', defaultWidth: 200 },
]

const HEAD_CLASS = 'text-left px-4 py-3 font-medium text-table-header-foreground'

const dateTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })

export function CollectionPage() {
  const navigate = useNavigate()
  const { can } = usePermission()
  const patientsOn = useFeatureFlag(FEATURES.patients)
  // The Start dialog reads patients and Collection forms, then creates a
  // submission, so it needs all three rights and the Patients module.
  const canStart =
    patientsOn &&
    can(PERMISSIONS.submissionsCreate) &&
    can(PERMISSIONS.proceduresRead) &&
    can(PERMISSIONS.formsRead)
  const cols = useTableColumns(COLLECTION_TABLE_ID, COLLECTION_COLUMNS)
  const [submissions, setSubmissions] = useState<SubmissionWithFormName[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [mrnFilter, setMrnFilter] = useState('')
  const [debouncedMrn, setDebouncedMrn] = useState('')
  const [skip, setSkip] = useState(0)
  const [take, setTake] = useState(20)
  const [startOpen, setStartOpen] = useState(false)

  useEffect(() => {
    // Guard prevents the mount-time timer from resetting pagination 300ms in.
    if (mrnFilter === debouncedMrn) return
    const id = setTimeout(() => { setDebouncedMrn(mrnFilter); setSkip(0) }, 300)
    return () => clearTimeout(id)
  }, [mrnFilter, debouncedMrn])

  // A new sort is a new ordering of the whole list, so page 1 again.
  const sort = sortParam(cols.sort)
  const [sortOfPage, setSortOfPage] = useState(sort)
  if (sortOfPage !== sort) {
    setSortOfPage(sort)
    setSkip(0)
  }

  // Only the newest request may write the list, so a slow response for an
  // older filter cannot replace the rows of a newer one.
  const requestRef = useRef(0)
  const load = useCallback(() => {
    const request = ++requestRef.current
    setLoading(true)
    setError(null)
    formsApi
      .listAllSubmissions({
        category: 'collection',
        q: debouncedMrn,
        skip,
        take,
        sort,
        definition: 'name',
        patientNames: true,
      })
      .then(({ items, total: t }) => {
        if (request !== requestRef.current) return
        setSubmissions(items)
        setTotal(t)
      })
      .catch(e => {
        if (request === requestRef.current) setError(e instanceof Error ? e.message : 'Could not load Collection forms.')
      })
      .finally(() => {
        if (request === requestRef.current) setLoading(false)
      })
  }, [debouncedMrn, skip, take, sort])

  useEffect(() => { load() }, [load])

  const open = useCallback((sub: FormSubmission) => navigate(`/forms/${sub.id}`), [navigate])

  return (
    <>
      <div className='w-full px-6 py-8'>
        <div className='flex items-center justify-between mb-6'>
          <PageHeading title='Collection' description='Collection forms for every patient. Filter by MRN to narrow results.' />
          <div className='flex items-center gap-2'>
            <Button variant='outline' size='sm' onClick={load} disabled={loading}>
              Refresh
            </Button>
            {canStart && (
              <Button size='sm' onClick={() => setStartOpen(true)}>
                <Plus className='mr-1.5 h-4 w-4' />
                Start Collection form
              </Button>
            )}
          </div>
        </div>

        <div className='mb-4 flex items-center justify-between gap-2'>
          <div className='relative max-w-xs flex-1'>
            <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
            <Input
              className='pl-9'
              placeholder='Filter by patient MRN…'
              value={mrnFilter}
              onChange={e => setMrnFilter(e.target.value)}
            />
          </div>
        </div>

        {loading && submissions.length === 0 && (
          <div className='flex items-center justify-center py-20'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        )}

        {error && (
          <div className='flex items-center gap-2 text-sm text-destructive rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 mb-4'>
            <AlertCircle className='h-4 w-4 shrink-0' />
            {error}
          </div>
        )}

        {!loading && total === 0 && (
          <div className='rounded-lg border border-dashed py-16 text-center text-muted-foreground'>
            <p className='text-sm'>
              {debouncedMrn ? 'No Collection forms match that MRN.' : 'No Collection forms yet.'}
            </p>
          </div>
        )}

        {total > 0 && (
          <div className={cn('border rounded-lg overflow-hidden', loading && 'opacity-60')}>
            <table className='w-full text-sm'>
              <thead className='bg-table-header border-b'>
                <tr>
                  {cols.visible.map(({ def }) => (
                    <ResizableTh
                      key={def.id}
                      columnId={def.id}
                      columns={cols}
                      label={def.header}
                      className={HEAD_CLASS}
                    >
                      {def.header}
                    </ResizableTh>
                  ))}
                </tr>
              </thead>
              <tbody>
                {submissions.map((sub, i) => {
                  const formName = sub.formDefinition?.name ?? 'Form'
                  return (
                    <tr
                      key={sub.id}
                      role='link'
                      tabIndex={0}
                      aria-label={`Open ${formName} for ${sub.patientName ?? sub.patientMrn ?? 'patient'}`}
                      onClick={() => open(sub)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          open(sub)
                        }
                      }}
                      className={cn(
                        'cursor-pointer border-b align-top transition-colors last:border-0 hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                        i % 2 === 1 && 'bg-muted/10',
                      )}
                    >
                      <ColumnCells
                        columns={cols}
                        cells={{
                          patientMrn: (
                            <td className='px-4 py-3'>
                              <div className='font-medium'>{sub.patientName ?? sub.patientMrn ?? '—'}</div>
                              {sub.patientName && sub.patientMrn && (
                                <div className='font-mono text-xs text-muted-foreground'>{sub.patientMrn}</div>
                              )}
                            </td>
                          ),
                          form: (
                            <td className='px-4 py-3'>
                              <span className='font-medium'>{formName}</span>
                              {sub.formDefinition?.version !== undefined && (
                                <span className='ml-2 rounded border px-1.5 py-0.5 text-[11px] text-muted-foreground'>
                                  V{sub.formDefinition.version}
                                </span>
                              )}
                            </td>
                          ),
                          status: (
                            <td className='px-4 py-3'>
                              <StatusBadge status={sub.status} />
                            </td>
                          ),
                          created: (
                            <td className='whitespace-nowrap px-4 py-3 text-muted-foreground'>
                              {dateTime(sub.createdAt)}
                            </td>
                          ),
                          updated: (
                            <td className='whitespace-nowrap px-4 py-3 text-muted-foreground' title={dateTime(sub.updatedAt)}>
                              {formatRelativeTime(sub.updatedAt)}
                            </td>
                          ),
                          completed: (
                            <td className='px-4 py-3'>
                              {sub.completedAt ? (
                                <>
                                  <div>{sub.completedBy ?? '—'}</div>
                                  <div className='text-xs text-muted-foreground'>{dateTime(sub.completedAt)}</div>
                                </>
                              ) : (
                                <span className='text-muted-foreground'>—</span>
                              )}
                            </td>
                          ),
                        }}
                      />
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <div className='flex items-center justify-between border-t bg-muted/20 px-4 py-2 text-xs text-muted-foreground'>
              <span>{`${skip + 1}–${Math.min(skip + take, total)} of ${total} Collection forms`}</span>
              <div className='flex items-center gap-2'>
                <select
                  value={take}
                  onChange={e => { setTake(Number(e.target.value)); setSkip(0) }}
                  className='rounded border bg-background px-2 py-0.5 text-xs'
                >
                  {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n} / page</option>)}
                </select>
                <div className='flex items-center gap-1'>
                  <button
                    className='inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-accent disabled:opacity-40'
                    disabled={skip === 0}
                    onClick={() => setSkip(s => Math.max(0, s - take))}
                  >
                    <ChevronLeft className='h-3.5 w-3.5' />
                    Previous
                  </button>
                  <button
                    className='inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-accent disabled:opacity-40'
                    disabled={skip + take >= total}
                    onClick={() => setSkip(s => s + take)}
                  >
                    Next
                    <ChevronRight className='h-3.5 w-3.5' />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Starting a form opens it on its own page, like a row does. */}
      <StartCollectionFormDialog
        open={startOpen}
        onClose={() => setStartOpen(false)}
        onStarted={sub => open(sub)}
      />
    </>
  )
}
